import { FunctionComponent } from 'react'
import Skeleton from 'react-loading-skeleton'
import { Box, Typography, Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import EmptyStateAction, { EmptyAction } from './EmptyStateAction'
import { Info, DollarSign } from 'react-feather'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js'
import colors from '../../colors'
import { SPACING } from '../../constants'
import { MonthlyRevenue } from '../../types'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
)

type RevenueChartCardProps = {
  emptyAction?: EmptyAction,
  data: MonthlyRevenue[]
  loading: boolean
}

const RevenueChartCard: FunctionComponent<RevenueChartCardProps> = ({ data, loading, emptyAction }) => {
  const styles = useStyles()

  const InfoIcon: FunctionComponent = () => {
    return (
      <Box className={styles.infoContainer}>
        <Tooltip title="Monthly revenue received over the last 12 months">
          <Info size={16} strokeWidth={3} color={colors.GREY_3} />
        </Tooltip>
      </Box>
    )
  }

  const chartData = {
    labels: data.map((d) => d.month),
    datasets: [
      {
        label: 'Revenue',
        data: data.map((d) => d.revenue),
        borderColor: colors.STITCH_BLUE,
        backgroundColor: 'rgba(17, 51, 85, 0.1)',
        fill: true,
        tension: 0.4,
        pointBackgroundColor: colors.STITCH_BLUE,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y
            return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value: any) => `$${value.toLocaleString()}`,
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
    },
  }

  const totalRevenue = data.reduce((sum, d) => sum + d.revenue, 0)

  return (
    <Box className={styles.container}>
      <Box className={styles.titleContainer}>
        <DollarSign size={20} color={colors.GREY_3} style={{ marginRight: SPACING }} />
        <Typography variant="h6">Revenue - Trailing 12 Months</Typography>
        <InfoIcon />
      </Box>

      {loading ? (
        <Box mt={2}>
          <Skeleton height={250} />
        </Box>
      ) : data.length === 0 ? (
        <Box className={styles.emptyState}>
          <Typography variant="body2" color="textSecondary">
            Your revenue will appear here after your first payment.
          </Typography>
          <EmptyStateAction action={emptyAction} />
        </Box>
      ) : (
        <>
          <Box className={styles.totalRevenue}>
            <Typography variant="body2" color="textSecondary">
              Total
            </Typography>
            <Typography variant="h5" className={styles.totalValue}>
              ${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Box>
          <Box className={styles.chartContainer}>
            <Line data={chartData} options={chartOptions} />
          </Box>
        </>
      )}
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    borderRadius: 12,
    boxShadow: '0px 0px 4px rgba(17, 51, 85, 0.15)',
    padding: SPACING * 3,
    backgroundColor: '#fff',
    width: '100%',
    minWidth: 0,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: SPACING,
  },
  infoContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    cursor: 'pointer',
  },
  totalRevenue: {
    marginBottom: SPACING * 2,
  },
  totalValue: {
    fontWeight: 600,
    color: colors.STITCH_BLUE,
  },
  chartContainer: {
    height: 250,
  },
  emptyState: {
    padding: SPACING * 4,
    display: 'flex',
    // Column, so an empty-state action sits under the message rather than
    // running on from the end of the sentence.
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 2,
    textAlign: 'center',
  },
})

export default RevenueChartCard
